import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import { withTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { isEmpty, isObject, cloneDeep } from 'lodash';
import * as roomActions from 'features/room-builder/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as taskActions from 'features/task-management/duck';
import * as materialChainActions from 'features/material-chain/duck';
import { ROOM_STATUS } from 'shared/constants';
import { mapRoomTypeOptions } from 'shared/selectors';
import {
  isRoomOnline,
  mapTypeOptions,
  hasRoomMaterialsRequired,
  mapTimingOptions,
} from 'shared/helpers';
import {
  MultiRadio,
  AlertMessage,
  AlertNotice,
  ModalInfo,
  Modal as WarningModal,
} from 'shared/components';
import CheckedCircleBoldIcon from 'assets/svgs/checkedCircleBold.svg';
import './OpenRoom.scss';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
};

const getSelectedType = (roomType, types) => {
  return types.find((t) => t.id === roomType.id) || {};
};

class OpenRoom extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timingOptions: [],
      typeOptions: [],
      room: props.room,
      errorModalOpen: false,
      mismatchModalOpen: false,
      errorRanges: false,
      errors: {},
      timingWarningModalOpen: false,
      serverError: false,
      selectedRoomType: '',
      isSubmitting: false,
    };

    this.onRoomTypeChange = this.onRoomTypeChange.bind(this);
    this.onTimingChange = this.onTimingChange.bind(this);
    this.toggleEdit = this.toggleEdit.bind(this);
    this.checkIsValid = this.checkIsValid.bind(this);
    this.checkMismatch = this.checkMismatch.bind(this);
    this.onUpdateRoom = this.onUpdateRoom.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.closeMismatchModal = this.closeMismatchModal.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.removeErrorNotice = this.removeErrorNotice.bind(this);
    this.openReviewRoom = this.openReviewRoom.bind(this);
    this.checkRoomTypeMaterialsMatch = this.checkRoomTypeMaterialsMatch.bind(this);
    this.setMaterialAlertType = this.setMaterialAlertType.bind(this);
    this.closeTimingWarningModal = this.closeTimingWarningModal.bind(this);
    this.checkTimingChange = this.checkTimingChange.bind(this);
    this.handleServerError = this.handleServerError.bind(this);
  }

  componentDidMount() {
    if (this.props.roomTypes.length && !this.props.materialTypesByRoomTypes.length) {
      this.props.actions.getMaterialTypesByRoomTypes(
        this.props.roomTypes.filter((type) => !type.onlineFlag)
      );
    }
  }

  componentWillUnmount() {
    this.props.actions.clearMaterialTypesByRoomTypesAction();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.warningModalOpen && this.props.warningModalOpen !== prevProps.warningModalOpen) {
      const selected =
        this.props.roomTypes.find((item) => item.id === this.props.room.roomType.id) ||
        this.props.roomTypes[0];
      const timingOptions = mapTimingOptions(
        this.props.roomTypes,
        this.props.mappedRoomTiming,
        selected.id
      );

      this.setState({
        typeOptions: mapTypeOptions(
          this.props.roomTypes,
          prevState.room.roomType.onlineFlag,
          null,
          null,
          this.props?.tcEvent?.studyFlag
        ),
        selectedRoomType: selected.id,
        timingOptions,
        room: {
          ...prevState.room,
          timing: this.props.room.timing,
        },
      });
    }
  }

  async openReviewRoom() {
    const { actions, room, roomTypes } = this.props;

    actions.getRoom(room.id);
    if (!isRoomOnline(room.roomType) && hasRoomMaterialsRequired(room.roomType)) {
      actions.getRoomMaterials(room.id);
      if (roomTypes.length && !this.props.materialTypesByRoomTypes.length) {
        this.props.actions.getMaterialTypesByRoomTypes(
          roomTypes.filter((type) => !type.onlineFlag)
        );
      }
    }

    this.setState({
      errorRanges: false,
    });

    this.props.toggleReviewRoomModal();
  }

  onTimingChange(e) {
    const { value } = e.target;

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        timing: value,
      },
      errors: {
        ...prevState.errors,
        timing: '',
      },
    }));
  }

  onRoomTypeChange(e) {
    const { value } = e.target;
    const type = this.props.roomTypes.find((type) => type.id === +value);
    const timingOptions = mapTimingOptions(
      this.props.roomTypes,
      this.props.mappedRoomTiming,
      value
    );

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        roomType: type,
        timing: '',
      },
      selectedRoomType: +value,
      timingOptions,
      errors: {
        ...prevState.errors,
        type: '',
      },
    }));
  }

  checkRoomTypeMaterialsMatch(materialGroup) {
    const { materialTypesByRoomTypes } = this.props;
    const { room } = this.state;

    if (
      materialTypesByRoomTypes.length &&
      room.roomType?.id &&
      materialTypesByRoomTypes[room.roomType.id]
    ) {
      return materialTypesByRoomTypes[room.roomType.id].some(
        (type) => type.id === materialGroup.id
      );
    }

    return;
  }

  setMaterialAlertType() {
    const { roomGroupedMaterials, materialTypesByRoomTypes } = this.props;
    const { room } = this.state;

    if (!roomGroupedMaterials.length || !room.roomType || !room.roomType.id) {
      return 'warning';
    }
    const warning =
      materialTypesByRoomTypes.length &&
      roomGroupedMaterials.some(
        (group) =>
          (!group.roomMaterials.length && this.checkRoomTypeMaterialsMatch(group)) ||
          (group.roomMaterials.length && !this.checkRoomTypeMaterialsMatch(group))
      );
    return warning ? 'warning' : 'info';
  }

  async submitFirstTask(roomId) {
    const { currentTask } = this.props;
    const completedTask = await this.props.actions.setCompleteTask(roomId, currentTask);

    if (!completedTask.id) {
      this.handleServerError(completedTask.data?.message);
      if (completedTask.status === 400) {
        this.props.actions.getRoomStatus(this.props.match.params.roomId);
      }
    }
  }

  checkIsValid() {
    const { room } = this.state;
    const { roomGroupedMaterials } = this.props;
    let flagError = false;
    let errors = { ...this.state.errors };

    if (!room.roomType) {
      errors.type = ['errors.roomTypeRequired'];
      flagError = true;
    }

    if (!room.timing) {
      errors.timing = ['errors.timingCategoryRequired'];
      flagError = true;
    }

    if (
      !isRoomOnline(room.roomType) &&
      hasRoomMaterialsRequired(room.roomType) &&
      (!roomGroupedMaterials.length || !roomGroupedMaterials[0].roomMaterials.length)
    ) {
      this.setState({
        errorRanges: true,
      });
      flagError = true;
    }

    if (flagError) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          ...errors,
        },
      }));
      return false;
    }

    return true;
  }

  checkTimingChange() {
    const updatedRoom = this.state.room;
    const { room } = this.props;

    return updatedRoom.timing !== room.timing;
  }

  async checkMismatch(e, skipTimingCheck) {
    // Disable the button
    this.setState({ isSubmitting: true });

    const room = cloneDeep(this.state.room);
    const { actions } = this.props;

    if (!this.checkIsValid()) {
      return;
    }

    if (!skipTimingCheck && this.checkTimingChange()) {
      this.setState({
        timingWarningModalOpen: true,
      });
      return;
    }

    const mismatch = await actions.checkRoomMismatch(room);
    if (mismatch) {
      this.setState({
        mismatchModalOpen: true,
        timingWarningModalOpen: false,
      });
    } else {
      this.onUpdateRoom();
    }
  }

  async onUpdateRoom() {
    const { actions, roomTypes } = this.props;
    const room = cloneDeep(this.state.room);

    await actions.updateRoomTypeTiming(room.id, {
      type: room.roomType,
      timing: room.timing,
    });
    if (this.props.errorTypeTiming) {
      this.handleServerError(this.props.errorTypeTiming);
      return;
    }
    actions.setRoomStatusAction({
      data: {
        roomTiming: room.timing,
        roomType: getSelectedType(room.roomType, roomTypes).name,
      },
    });
    this.setState({
      serverError: false,
    });

    await actions.updateRoomStatus(room.id, { status: ROOM_STATUS.opened });
    if (this.props.errorUpdateStatus) {
      this.handleServerError(this.props.errorUpdateStatus);
      return;
    } else {
      if (isRoomOnline(room.roomType) && !this.props.tcEvent.specialTestingFlag) {
        sessionStorage.setItem('onlineTestingRedirect', true);
      }
      this.submitFirstTask(room.id);
    }

    actions.setRoomStatusAction({
      data: {
        status: ROOM_STATUS.opened,
      },
    });
    this.setState({
      mismatchModalOpen: false,
      timingWarningModalOpen: false,
      serverError: false,
    });
    this.props.closeModal();
  }

  handleServerError(errorMessage) {
    if (errorMessage.status === 400) {
      this.setState({
        errorModalOpen: true,
        mismatchModalOpen: false,
        timingWarningModalOpen: false,
        errorMessage: isObject(errorMessage.error)
          ? errorMessage.error.message
          : errorMessage.error,
      });
      return;
    }

    this.setState({
      serverError: true,
      mismatchModalOpen: false,
      timingWarningModalOpen: false,
    });
  }

  toggleEdit(editSection) {
    this.setState({
      [editSection]: true,
    });
  }

  closeModal() {
    const { room } = this.props;
    this.setState({
      timingWarningModalOpen: false,
      room,
      errors: {},
      isSubmitting: false,
    });

    this.props.closeModal();
  }

  closeMismatchModal() {
    this.props.history.push(
      `/test-event/${this.props.tcEvent.id}/room/${this.props.room.id}/roster?redirected=fromDashboard`,
      { edit: true, from: window.location.pathname }
    );
    this.setState({
      mismatchModalOpen: false,
    });
  }

  closeTimingWarningModal() {
    this.setState({
      timingWarningModalOpen: false,
      isSubmitting: false,
    });
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  removeErrorNotice() {
    this.setState({
      errorModalOpen: false,
    });
    this.props.actions.getRoomStatus(this.props.room.id);
    this.closeModal();
  }

  render() {
    const {
      room,
      errors,
      showMessage,
      errorModalOpen,
      mismatchModalOpen,
      errorMessage,
      errorRanges,
      timingWarningModalOpen,
      serverError,
      selectedRoomType,
      isSubmitting,
    } = this.state;
    const { roomGroupedMaterials, materialTypesByRoomTypes, isInactive } = this.props;
    let roomMaterialsMessage;
    const materialAlertType = this.setMaterialAlertType();

    if (!isRoomOnline(room.roomType) && hasRoomMaterialsRequired(room.roomType)) {
      if (roomGroupedMaterials.length) {
        const isMatchedWithRoomType = roomGroupedMaterials.some(
          (materialGroup) =>
            !this.checkRoomTypeMaterialsMatch(materialGroup) && materialGroup.roomMaterials.length
        );
        roomMaterialsMessage = isMatchedWithRoomType
          ? this.props.t('materialChain.materialNotMatchRoomType')
          : this.props.t('roomDashboard.materialChain.infoMessage');
      } else {
        roomMaterialsMessage = this.props.t('roomDashboard.materialChain.noTestMaterials');
      }
    }

    if (showMessage) {
      return (
        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message="errors.roomTypeAndTimingAlreadySelected"
          isError
        />
      );
    }

    return (
      !isEmpty(room) && (
        <div className="open-room-container">
          {isInactive && (
            <button className="btn btn-small btn-primary" onClick={this.openReviewRoom}>
              <CheckedCircleBoldIcon className="checked-circle-icon" />
              {this.props.t('taskManagement.completeOpenRoom')}
            </button>
          )}
          <ModalInfo
            modalIsOpen={this.props.warningModalOpen}
            closeModal={this.closeModal}
            onSubmit={this.checkMismatch}
            className="edit-room-info-modal"
            headerTitle="taskManagement.reviewRoom"
            saveLabel="taskManagement.confirmAndOpenRoom"
            disabledSubmit={isSubmitting}>
            <div>
              <div className="edit-room-information">
                <p className="fw-bold">{this.props.t('taskManagement.reviewRoomMessage')}</p>
                <AlertNotice
                  message={this.props.t('taskManagement.reviewRoomInfo')}
                  type="warning"
                />

                <div className="mb-30">
                  <div className="d-inline-block">
                    <span className="mt-0 fw-bold">
                      {this.props.t('roomBuilder.roomTypeLabel')}
                    </span>
                    <div>
                      <div className="mt-3">
                        <MultiRadio
                          radioList={this.state.typeOptions}
                          checkedValue={selectedRoomType}
                          onChange={this.onRoomTypeChange}
                          errors={errors.type}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {this.state.timingOptions.length && (
                  <div className="mb-30">
                    <div className="d-inline-block">
                      <span className="fw-bold">
                        {this.props.t('taskManagement.roomTimingLabel')}
                      </span>
                      <div>
                        <div className="mt-3">
                          <MultiRadio
                            radioList={this.state.timingOptions}
                            checkedValue={room.timing}
                            onChange={this.onTimingChange}
                            errors={errors.timing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!isRoomOnline(room.roomType) && hasRoomMaterialsRequired(room.roomType) && (
                <div className="open-room-materials border-top-gray">
                  <p className="fw-bold">{this.props.t('roomDashboard.materialChain.title')} </p>
                  <AlertNotice message={roomMaterialsMessage} type={materialAlertType} />
                  {!roomGroupedMaterials.length && errorRanges && (
                    <span className="text-error">{this.props.t('errors.rangesRequired')}</span>
                  )}
                  <div>
                    {roomGroupedMaterials.map((materialGroup) => {
                      if (
                        !materialGroup.roomMaterials.length &&
                        room.roomType &&
                        materialTypesByRoomTypes.length &&
                        !this.checkRoomTypeMaterialsMatch(materialGroup)
                      ) {
                        return null;
                      }
                      return (
                        <div key={materialGroup.id} className="material-group">
                          <p className="mb-2 fw-bold">
                            {materialGroup.name}
                            {!this.checkRoomTypeMaterialsMatch(materialGroup) &&
                              !!materialGroup.roomMaterials.length && (
                                <i className="fa fa-exclamation-triangle pe-2" />
                              )}
                          </p>
                          {materialGroup.name === 'MC booklet' && errorRanges && (
                            <span className="text-error">
                              {this.props.t('errors.rangesRequired')}
                            </span>
                          )}
                          {materialGroup.roomMaterials.length ? (
                            materialGroup.roomMaterials.map((material, idx) => (
                              <div key={idx}>
                                {material.rangeBegin} - {material.rangeEnd} ({material.count})
                              </div>
                            ))
                          ) : (
                            <div>
                              <i className="fa fa-exclamation-triangle pe-2" />
                              {materialGroup.name === 'Writing booklet' && !room.roomType
                                ? this.props.t('roomDashboard.materialChain.selectRoomType')
                                : this.props.t('roomDashboard.materialChain.noTestBooklets')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {serverError && (
                <AlertNotice message={this.props.t('errors.somethingWentWrong')} type="error" />
              )}
            </div>
          </ModalInfo>
          <Modal
            isOpen={errorModalOpen}
            onRequestClose={this.removeErrorNotice}
            shouldCloseOnOverlayClick={false}
            style={customStyles}
            ariaHideApp={false}>
            <div className="text-center warning-container">
              <p>{errorMessage}</p>
              <button className="btn btn-warn text-center" onClick={this.removeErrorNotice}>
                {this.props.t('common.labels.ok')}
              </button>
            </div>
          </Modal>
          <ModalInfo
            modalIsOpen={mismatchModalOpen}
            onCancel={this.onUpdateRoom}
            onSubmit={this.closeMismatchModal}
            className="mismatch-modal"
            saveLabel="taskManagement.manageMismatch"
            cancelLabel="taskManagement.openRoomModalLabel"
            containerClass="mismatch-modal-container"
            showCancel>
            <h4>{this.props.t('taskManagement.openRoomMismatchTitle')}</h4>
            <p>{this.props.t('taskManagement.openRoomMismatchMessage1')}</p>
            <p>{this.props.t('taskManagement.openRoomMismatchMessage2')}</p>
          </ModalInfo>
          <WarningModal
            closeModal={this.closeTimingWarningModal}
            hideIcon
            onConfirm={(e) => this.checkMismatch(e, true)}
            open={timingWarningModalOpen}
            modalContent="roomBuilder.timingWarningModal"
          />
        </div>
      )
    );
  }
}

function mapStateToProps(state) {
  return {
    room: state.roomOptions.room || {},
    roomTypes: state.statics.roomTypes,
    tasks: state.taskManagement.tasks || [],
    errorUpdateStatus: state.roomOptions.errorUpdateStatus,
    materialTypesByRoomTypes: state.materialChain.materialTypesByRoomTypes,
    roomTypeOptions: mapRoomTypeOptions(state, this.props?.tcEvent?.studyFlag),
    errorTypeTiming: state.roomOptions.errorTypeTiming,
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...roomActions,
        ...staticActions,
        ...taskActions,
        ...materialChainActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(OpenRoom))
);

OpenRoom.propTypes = {
  // Props from parent component
  room: PropTypes.shape({
    id: PropTypes.number,
    roomType: PropTypes.shape({
      id: PropTypes.number,
      onlineFlag: PropTypes.bool,
    }),
    timing: PropTypes.string,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  warningModalOpen: PropTypes.bool,
  toggleReviewRoomModal: PropTypes.func,

  // Props from react-router (via withRouterLegacy)
  match: PropTypes.shape({
    params: PropTypes.shape({
      roomId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,

  // Props from mapStateToProps
  roomTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      onlineFlag: PropTypes.bool,
    })
  ).isRequired,
  tasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    studyFlag: PropTypes.bool,
    specialTestingFlag: PropTypes.bool,
  }).isRequired,
  currentTask: PropTypes.object,
  roomGroupedMaterials: PropTypes.array,
  materialTypesByRoomTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isInactive: PropTypes.bool,
  roomTypeOptions: PropTypes.array,
  errorUpdateStatus: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  errorTypeTiming: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  mappedRoomTiming: PropTypes.object,

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    updateRoomTypeTiming: PropTypes.func.isRequired,
    getMaterialTypesByRoomTypes: PropTypes.func.isRequired,
    clearMaterialTypesByRoomTypesAction: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    getRoomMaterials: PropTypes.func.isRequired,
    setCompleteTask: PropTypes.func.isRequired,
    getRoomStatus: PropTypes.func.isRequired,
    checkRoomMismatch: PropTypes.func.isRequired,
    setRoomStatusAction: PropTypes.func.isRequired,
    updateRoomStatus: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

OpenRoom.defaultProps = {
  warningModalOpen: false,
};
